import Cookies from "js-cookie";
import {
  createContext,
  type ReactNode,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { OT_SHOP_TYPE_COOKIE } from "src/utils/constants";
import { createReducer, type Dispatch } from "src/utils/create-reducer";
import {
  localeFixOptional,
  ShopType,
} from "src/utils/genderLocale/genderLocale";

export type ShopTypeState = {
  currentShopType: string;
};

export type ShopTypeActions = { type: "SET_SHOP_TYPE"; payload: string };

export type UseShopTypeContext = {
  genderState: ShopTypeState;
  pageViewedDispatch: Dispatch<ShopTypeActions>;
};

export const ShopTypeContext = createContext<ShopTypeState>({
  currentShopType: "",
});

export const ShopTypeDispatch = createContext<
  Dispatch<ShopTypeActions> | undefined
>(undefined);

export const shopTypeReducer = createReducer<ShopTypeState, ShopTypeActions>({
  SET_SHOP_TYPE: (state, payload) => ({ ...state, currentShopType: payload }),
});

export function ShopTypeContextProvider({
  children,
  gender,
  preferredShopType,
  isCartOrFavorites,
}: {
  gender?: string;
  preferredShopType?: string;
  children: ReactNode;
  isCartOrFavorites: boolean;
}): JSX.Element {
  const cookieShopType = useMemo(() => Cookies.get(OT_SHOP_TYPE_COOKIE), []);
  const userDefaultShopType = useMemo(() => {
    if (!preferredShopType) {
      return;
    }

    return preferredShopType === "men" ? "men" : "women";
  }, [preferredShopType]);

  const shopTypeFromPage = useMemo(() => {
    if (gender) {
      return localeFixOptional({ gender }) || ShopType.All;
    }

    const persisted = cookieShopType || userDefaultShopType || ShopType.Women;

    return !isCartOrFavorites && persisted === "kids"
      ? ShopType.Women
      : persisted;
  }, [gender, cookieShopType, userDefaultShopType, isCartOrFavorites]);

  const [shopTypeState, shopTypeDispatch] = useReducer(shopTypeReducer, {
    currentShopType: shopTypeFromPage,
  });

  const prevGenderRef = useRef(gender);
  if (gender !== prevGenderRef.current) {
    prevGenderRef.current = gender;
    if (gender) {
      shopTypeDispatch({ type: "SET_SHOP_TYPE", payload: shopTypeFromPage });
    }
  }

  return (
    <ShopTypeContext.Provider value={shopTypeState}>
      <ShopTypeDispatch.Provider value={shopTypeDispatch}>
        {children}
      </ShopTypeDispatch.Provider>
    </ShopTypeContext.Provider>
  );
}
